import { safeLocalGet, safeLocalSet } from "@/lib/browser-storage";

// Client-side birth-details memory ("enter once, reuse everywhere" — the
// browser-autofill / "remember me" pattern, sibling to checkout-prefill.ts).
//
// WHY this and not a server fetch: the seeker hands over name / DOB / time /
// place on the first form they touch (the kundali form OR a self-chart
// calculator). Every subsequent SELF-chart surface should prefill those instead
// of re-asking (rule 39 — minimize friction; the owner's "don't ask it again and
// again"). The existing reading-prefill (KundaliFormCard.prefill.ts) only fills
// from URL params (nudge landings / calculator→kundali handoff links), so organic
// navigation between calculators still re-asks. Storing the details on-device
// makes the prefill INSTANT (no network call → no CWV/LCP cost, rule 62) and
// works regardless of how the seeker navigated.
//
// PRIVACY: the DOB/place stay on the user's own device (localStorage), never in a
// URL, page render, referrer, log, or API response (same posture as
// checkout-prefill.ts). Only SELF-chart forms write/read this — never the baby
// name (the child's details), matching Person B (a partner), or friend surfaces.

const KEY = "ishvaram_birth_details";

export type SavedBirthDetails = {
  name?: string;
  birth_date?: string;
  birth_time?: string;
  birth_place?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
};

function cleanText(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  return value.trim() || undefined;
}

function cleanCoordinates(d: SavedBirthDetails, birthPlace?: string): SavedBirthDetails {
  if (!birthPlace || typeof d.latitude !== "number" || typeof d.longitude !== "number") return {};
  return {
    latitude: d.latitude,
    longitude: d.longitude,
    ...(cleanText(d.timezone) ? { timezone: cleanText(d.timezone) } : {}),
  };
}

function compact(details: SavedBirthDetails): SavedBirthDetails {
  return Object.fromEntries(
    Object.entries(details).filter(([, value]) => value !== undefined)
  ) as SavedBirthDetails;
}

function clean(d: SavedBirthDetails): SavedBirthDetails {
  const birthPlace = cleanText(d.birth_place);
  return compact({
    name: cleanText(d.name),
    birth_date: cleanText(d.birth_date),
    birth_time: cleanText(d.birth_time),
    birth_place: birthPlace,
    ...cleanCoordinates(d, birthPlace),
  });
}

/** Merge-and-persist the entered birth details. No-op if nothing usable is present. */
export function saveBirthDetails(next: SavedBirthDetails): void {
  const merged = clean({ ...getBirthDetails(), ...next });
  if (!merged.name && !merged.birth_date && !merged.birth_time && !merged.birth_place) return;
  safeLocalSet(KEY, JSON.stringify(merged));
}

/** Read the stored birth details. Returns {} on server, missing, or malformed. */
export function getBirthDetails(): SavedBirthDetails {
  const raw = safeLocalGet(KEY);
  if (!raw) return {};
  try {
    return clean(JSON.parse(raw) as SavedBirthDetails);
  } catch {
    return {};
  }
}
